'use client'

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { getLocationState, setManualLocation } from '@/app/lib/geolocation'
import { ALL_LOCATIONS } from '@/app/lib/constants'

interface CityContextValue {
  /** Slug de la localización activa. Por defecto `todos`. */
  location: string
  /** Cambia la localización y la persiste en la cookie `location`. */
  setLocation: (location: string) => void
}

// `null` por defecto: usar el hook fuera del provider es un error de
// programación y debe fallar de forma explícita, no devolver un valor silencioso.
const CityContext = createContext<CityContextValue | null>(null)

interface CityProviderProps {
  children: ReactNode
  /**
   * Override explícito del valor inicial (uso en tests). En producción no se
   * pasa: el servidor y el primer render de cliente siempre arrancan en
   * `ALL_LOCATIONS` (sin depender de la cookie `location`, para no sacar a la
   * página del renderizado estático — ver
   * specs/010-mejora-rendimiento-web/research.md §2), y un efecto posterior al
   * montaje relee la cookie real y ajusta el estado si difiere.
   */
  initialLocation?: string
}

export function CityProvider({ children, initialLocation = ALL_LOCATIONS }: CityProviderProps) {
  const [location, setLocationState] = useState(initialLocation)

  useEffect(() => {
    // No se puede resolver durante el render (ni siquiera condicionado): leer
    // la localización real aquí correría también en el primer render de
    // cliente (el de la hidratación) y podría no coincidir con el HTML del
    // servidor, rompiendo la hidratación. Debe esperar a después de montar.
    //
    // La cookie `location` es `HttpOnly` (la escribe el servidor —
    // `setManualLocation`/`src/proxy.ts`): `document.cookie` NUNCA la ve,
    // así que leerla con un `getCookie` de cliente siempre devuelve
    // `undefined` y esta corrección nunca se aplicaba (bug: el selector de
    // localización de `ProductosLocalizados` mostraba siempre "toda España"
    // al cargar, aunque la cookie real tuviera otra provincia guardada). Se
    // resuelve con la Server Action ya existente `getLocationState`
    // (`@/app/lib/geolocation`, specs/025-geolocalizacion-web-general) — la
    // misma vía que ya usa el resto del sitio para conocer la localización
    // real persistida.
    let cancelled = false
    getLocationState()
      .then(({ locationSlug }) => {
        if (cancelled) return
        setLocationState((current) => (current === locationSlug ? current : locationSlug))
      })
      .catch(() => {
        // Degrada en silencio: el valor por defecto (`ALL_LOCATIONS`) ya
        // está pintado — un fallo puntual de esta corrección no debe romper
        // la página ni dejar la localización en un estado peor que el
        // arranque SSR-safe.
      })
    return () => {
      cancelled = true
    }
  }, [])

  const setLocation = useCallback((next: string) => {
    setLocationState(next)
    // Persistencia real (cookie `HttpOnly`) — un `document.cookie` de
    // cliente no puede escribirla (ver comentario del efecto de arriba);
    // `setManualLocation` es la única vía. No bloquea la actualización del
    // estado local: la UI ya refleja `next` mientras esto termina.
    void setManualLocation(next)
  }, [])

  return <CityContext.Provider value={{ location, setLocation }}>{children}</CityContext.Provider>
}

export function useCityContext(): CityContextValue {
  const ctx = useContext(CityContext)
  if (!ctx) {
    throw new Error('useCityContext debe usarse dentro de <CityProvider>.')
  }
  return ctx
}
